import {
  AddIcon,
  Button,
  Checkbox,
  DeleteIcon,
  ErrorState,
  Grid,
  GridColumn,
  GridRow,
  Input,
  Modal,
  TabBar,
} from '@bp/ui-components';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { ModalBottomButtons } from '../../../ModalBottomButtons/ModalBottomButtons';
import style from './MergeSubjectContainerLessonsModal.module.scss';

type handleMergeParams = {
  lessonUuids: string[];
  cardsFromLesson?: string | null;
  newCards?: { duration: number; count: number }[];
};

type MergeSubjectContainerLessonsModalProps = {
  open: boolean;
  onClose: () => void;
  handleMerge: ({ lessonUuids, cardsFromLesson, newCards }: handleMergeParams) => Promise<void>;
  lessons: {
    uuid: string;
    name: string;
    subjectContainerUuid: string;
    subjectName: string;
    onlyInTimetableVersion: boolean;
    cards: ReactNode;
  }[];
  allHaveToBeInTimetableVersion?: boolean; // Optional prop to check if all lessons have to be in the same timetable version
};

export const MergeSubjectContainerLessonsModal = ({
  open,
  onClose,
  handleMerge,
  lessons,
  allHaveToBeInTimetableVersion,
}: MergeSubjectContainerLessonsModalProps) => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<'lessons' | 'newCards'>('lessons');
  const [selected, setSelected] = useState<string | null>(null);

  const [rows, setRows] = useState<
    {
      uuid: string;
      duration: number;
      count: number | null;
    }[]
  >([
    {
      uuid: v4(),
      duration: 1,
      count: null,
    },
  ]); // Initialize with one row
  const [preview, setPreview] = useState<boolean>(false);

  const onRequestClose = () => {
    setSelectedTab('lessons');
    setSelected(null);
    setRows([
      {
        uuid: v4(),
        count: null,
        duration: 1,
      },
    ]);
    onClose();
  };

  useEffect(() => {
    if (lessons) {
      setSelected(lessons[0]?.uuid || null);
    }
  }, [lessons]);

  const allFromOneSubjectContainer = lessons.every(
    (lesson) => lesson.subjectContainerUuid === lessons[0].subjectContainerUuid,
  );

  const allInTimetableVersion = lessons.every((lesson) => lesson.onlyInTimetableVersion);

  if (!allInTimetableVersion && allHaveToBeInTimetableVersion) {
    return (
      <Modal
        title={t('teachingBlock.merge')}
        width={'s'}
        isOpen={open}
        onRequestClose={onRequestClose}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
      >
        <ErrorState type='error' title={t('teachingBlock.allHaveToBeInTimetableVersion')} />
      </Modal>
    );
  }

  if (!allFromOneSubjectContainer) {
    return (
      <Modal
        title={t('teachingBlock.merge')}
        width={'s'}
        isOpen={open}
        onRequestClose={onRequestClose}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
      >
        <ErrorState type='error' title={t('teachingBlock.selectWithSameSubjectContainer')} />
      </Modal>
    );
  }

  return (
    <Modal title={t('teachingBlock.merge')} width={'s'} isOpen={open} onRequestClose={onRequestClose}>
      <p className={'mb-6'}>{t('teachingBlock.selectLessonOrCreateCards')}</p>
      <TabBar
        onValueChange={(value: string) => {
          setSelectedTab(value as 'lessons' | 'newCards');
        }}
        activeTabId={selectedTab}
        tabs={[
          {
            trigger: t('teachingBlock.title', { count: 1 }),
            id: 'lessons',
            content: (
              <Grid>
                {lessons.map((lesson) => {
                  return (
                    <GridRow key={lesson.uuid} spacingBottom={'s'} spacingTop={'s'} style={{ alignItems: 'center' }}>
                      <GridColumn width={4}>
                        <span
                          className={
                            lesson.subjectContainerUuid !== lessons[0].subjectContainerUuid ? 'strikethrough' : ''
                          }
                        >
                          {lesson.name}
                        </span>
                      </GridColumn>
                      <GridColumn width={4}>{lesson.cards}</GridColumn>
                      <GridColumn width={4} align={'end'}>
                        <Checkbox
                          disabled={lesson.subjectContainerUuid !== lessons[0].subjectContainerUuid}
                          name={lesson.name}
                          checked={lesson.uuid === selected}
                          onClick={() => {
                            setSelected(lesson.uuid === selected ? null : lesson.uuid);
                          }}
                        />
                      </GridColumn>
                    </GridRow>
                  );
                })}
              </Grid>
            ),
          },
          {
            trigger: t('lessonUnit.newCards'),
            id: 'newCards',
            content: (
              <Grid useFormGap>
                {rows.map((r, i) => {
                  return (
                    <GridRow spacingBottom={'s'} spacingTop={'s'} key={r.uuid} style={{ alignItems: 'center' }}>
                      <GridColumn width={5}>
                        <Input
                          type={'number'}
                          label={t('lessonUnit.duration')}
                          name={`duration`}
                          onChange={(e) => {
                            setRows((prev) => {
                              const newRows = [...prev];
                              newRows[i].duration = Number(e.target.value);
                              return newRows;
                            });
                          }}
                          value={r.duration}
                          min={1}
                          max={52}
                          suffix={t('common.week', {
                            count: r.duration ?? 0,
                          })}
                        />
                      </GridColumn>
                      <GridColumn width={5}>
                        <Input
                          type={'number'}
                          label={t('common.count') as string}
                          tooltipText={t('lessonUnit.tooltip.count') as string}
                          name={`count`}
                          onChange={(e) =>
                            setRows((prev) => {
                              const newRows = [...prev];
                              newRows[i].count = Number(e.target.value) !== 0 ? Number(e.target.value) : null;
                              return newRows;
                            })
                          }
                          value={r.count}
                        />
                      </GridColumn>
                      <GridColumn width={2} align={'end'}>
                        <Button
                          className={'mt-4'}
                          icon={<DeleteIcon className={'small'} />}
                          hierarchy={'tertiary'}
                          onClick={() => {
                            if (rows.length === 1) {
                              return; // Prevent removing the last row
                            }
                            setRows((prev) => prev.filter((row) => row.uuid !== r.uuid));
                          }}
                        ></Button>
                      </GridColumn>
                    </GridRow>
                  );
                })}
                <GridRow>
                  <GridColumn width={5}>
                    <Input
                      dense={true}
                      className={classNames(style.hidden, { [style['visible']]: preview })}
                      type={'number'}
                      name={`duration`}
                      value={1}
                      min={1}
                      max={52}
                      disabled={true}
                      suffix={t('common.week', {
                        count: 0,
                      })}
                    />
                  </GridColumn>
                  <GridColumn width={5}>
                    <Input
                      dense={true}
                      className={classNames(style.hidden, { [style['visible']]: preview })}
                      type={'number'}
                      disabled={true}
                      name={`count`}
                      value={null}
                    />
                  </GridColumn>
                  <GridColumn width={2} align={'end'}>
                    <Button
                      hierarchy={'secondary'}
                      icon={
                        <AddIcon
                          onMouseEnter={() => setPreview(true)}
                          onMouseLeave={() => setPreview(false)}
                          className={'small'}
                        />
                      }
                      onClick={() => {
                        setRows((prev) => [...prev, { uuid: v4(), duration: 1, count: null }]);
                      }}
                    ></Button>
                  </GridColumn>
                </GridRow>
              </Grid>
            ),
          },
        ]}
      />

      <ModalBottomButtons
        closeButton={{ callback: () => onRequestClose() }}
        submitButton={
          allFromOneSubjectContainer
            ? {
                disabled:
                  (!selected && selectedTab === 'lessons') ||
                  (!rows.length && !rows[0].count && selectedTab === 'newCards'),
                text: t('common.merge'),
                callback: async () => {
                  await handleMerge({
                    cardsFromLesson: selectedTab === 'lessons' ? selected : undefined,
                    lessonUuids: lessons.map((l) => l.uuid),
                    newCards:
                      selectedTab === 'newCards'
                        ? rows.map((r) => ({ count: r.count ?? 0, duration: r.duration }))
                        : [],
                  });
                },
              }
            : {
                text: t('common.close'),
                callback: () => {
                  onRequestClose();
                },
              }
        }
      />
    </Modal>
  );
};
